import { inject, provideEnvironmentInitializer } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  type Route,
  RedirectCommand,
  createUrlTreeFromSnapshot,
} from '@angular/router';

import { map, NEVER, type Observable, take } from 'rxjs';

import {
  type DialogConfigOptions,
  DialogWidth,
  type InferDialogData,
} from '@core/user-interface-legacy';
import { TellentUserRequestsViewComponent } from '@tellent-ask-hr/core-smart-ui';
import { AskTemplatesDataService } from '@tellent-ask-hr/templates-logic';
import {
  AskTemplateNotificationsService,
  CreateTicketDialogComponent,
  TellentAskTemplatesViewComponent,
  TellentUserTemplatesViewComponent,
} from '@tellent-ask-hr/templates-smart-ui';
import { TicketsDataService } from '@tellent-ask-hr/tickets-logic';
import {
  TellentAskTicketsViewComponent,
  TellentUserTicketsViewComponent,
  TicketDialogComponent,
} from '@tellent-ask-hr/tickets-smart-ui';

export const ROUTES = [
  {
    path: 'ask-hr',
    children: [
      { path: 'tickets', component: TellentAskTicketsViewComponent },
      { path: 'forms', component: TellentAskTemplatesViewComponent },
      {
        path: 'my',
        component: TellentUserRequestsViewComponent,
        children: [
          { path: 'forms', component: TellentUserTemplatesViewComponent },
          { path: 'tickets', component: TellentUserTicketsViewComponent },
          { path: '**', redirectTo: 'tickets' },
        ],
      },
      { path: '**', redirectTo: 'tickets' },
    ],
    providers: [provideEnvironmentInitializer(() => inject(AskTemplateNotificationsService))],
  },
  {
    path: 'ask-hr-create-ticket/:templateId',
    component: CreateTicketDialogComponent,
    data: {
      options: {
        width: DialogWidth.LargeX,
      } satisfies DialogConfigOptions,
    },
    resolve: {
      dialogData: (route: ActivatedRouteSnapshot) =>
        inject(AskTemplatesDataService)
          .loadTemplateById(+route.paramMap.get('templateId')!)
          .pipe(
            take(1),
            map(({ isSuccess, success }) => {
              if (!isSuccess)
                return new RedirectCommand(createUrlTreeFromSnapshot(route, ['/ask-hr/my/forms']));

              return { template: success! };
            }),
          ) satisfies Observable<InferDialogData<CreateTicketDialogComponent> | RedirectCommand>,
    },
    outlet: 'dialog',
    providers: [provideEnvironmentInitializer(() => inject(AskTemplateNotificationsService))],
  },
  {
    path: 'ask-hr-ticket/:ticketId',
    component: TicketDialogComponent,
    data: {
      options: {
        width: DialogWidth.LargeX,
      } satisfies DialogConfigOptions,
    },
    resolve: {
      dialogData: (route: ActivatedRouteSnapshot) => {
        const ticketDataService = inject(TicketsDataService);
        const ticketId = +route.paramMap.get('ticketId')!;
        return ticketDataService.loadTicketById(ticketId).pipe(
          take(1),
          map(({ isSuccess }) => {
            if (!isSuccess)
              return new RedirectCommand(createUrlTreeFromSnapshot(route, ['/ask-hr/my/tickets']));

            return { ticket: ticketDataService.getTicketById(ticketId) };
          }),
        );
      },
    },
    outlet: 'dialog',
  },
  {
    path: '**',
    canActivate: [() => NEVER],
    loadComponent: () => NEVER,
  },
] satisfies Route[];
